import { FC, useState } from 'react';
import FleetTitle from '@/libs/antd/components/Title';
import { getAdminCustomers } from '@/libs/fleet-api/services/admin';
import { useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	Flex,
	Form,
	Input,
	Row,
	Select,
	Space,
	Table,
} from 'antd';
import { FleetQueryParams } from '@/libs/fleet-api/fetcher';
import { useModals } from '@/components/modals/ModalProvider';
import dayjs from 'dayjs';
import ExtendExpiryModal, { useExtendExpiryModal } from './modals/ExtendExpiryModal';
import AssignFrmModal, { useAssignFrmModal } from './modals/AssignFrmModal';
import DeleteCustomerModal, { useDeleteCustomerModal } from './modals/DeleteCustomerModal';
import { DeleteOutlined } from '@ant-design/icons';

// Temporary types until they can be properly imported from fleet-api
type PaginationState = {
	pageNumber: number;
	pageSize: number;
};

const AdminCustomerListPage: FC = () => {
	const [searchBy, setSearchBy] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState<string | null>(null);
	const [pagination, setPagination] = useState<PaginationState>({
		pageNumber: 1,
		pageSize: 20,
	});
	const [queryParams, setQueryParams] = useState<FleetQueryParams>({
		search: undefined,
		filter: undefined,
		pagination,
		// myCustomer is forced to false in the API call for this admin endpoint
	});

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['getAdminCustomers', queryParams] as const,
		queryFn: ({ queryKey }) => {
			const [, params] = queryKey;
			return getAdminCustomers(params);
		},
	});

	const dataSource = data?.data.items ?? [];
	const { openCustomerDetail } = useModals();
	const extendExpiryModalHook = useExtendExpiryModal();
	const assignFrmModalHook = useAssignFrmModal();
	const deleteCustomerModalHook = useDeleteCustomerModal();

	const handleSearch = () => {
		setQueryParams((current: FleetQueryParams) => {
			if (!searchBy || !searchValue) {
				return {
					...current,
					search: undefined,
				};
			}
			return {
				...current,
				search: {
					by: searchBy,
					value: searchValue,
				},
			};
		});
	};

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size="large"
		>
			<FleetTitle>Customer Information (Admin)</FleetTitle>

			<Form
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={handleSearch}
			>
				<Row gutter={[20, 20]}>
					<Col flex="auto">
						<Form.Item
							label={<span>Search By</span>}
							labelCol={{ flex: 'auto' }}
							wrapperCol={{ flex: 'none', style: { width: 250 } }}
						>
							<Select
								placeholder="Search By"
								options={[
									{
										label: 'Nama Customer',
										value: 'customer-name',
									},
									{
										label: 'NPWP',
										value: 'npwp',
									},
								]}
								allowClear
								onChange={(value) => {
									setSearchBy(value);
								}}
								onClear={() => {
									setQueryParams((current: FleetQueryParams) => {
										return {
											...current,
											search: undefined,
										};
									});
									setSearchBy(null);
								}}
								style={{
									display: 'block',
									width: '100%',
								}}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						<Form.Item>
							<Input
								placeholder="Search..."
								style={{
									display: 'block',
								}}
								disabled={searchBy === null}
								value={searchValue || ''}
								onChange={(e) => {
									const value = e.target.value;
									setSearchValue(value);
								}}
							/>
						</Form.Item>
					</Col>
					<Col>
						<Form.Item>
							<Button
								block
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Table
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading || isFetching}
				dataSource={dataSource.map((item, key) => ({ ...item, key }))}
				columns={[
					{
						title: 'Customer ID',
						dataIndex: 'customerId',
					},
					{
						title: 'Nama Customer',
						dataIndex: 'customerName',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(record.customerId);
								}}
							>
								{record.customerName}
							</a>
						),
					},
					{
						title: 'Sumber',
						align: 'center',
						render: (_, record) =>
							record.source === 'ESTARLOS' ? 'ESTAR' : record.source,
					},
					{
						title: 'Status Customer',
						width: 1,
						align: 'center',
						dataIndex: 'status',
					},
					{
						title: 'Group Customer BMPP',
						dataIndex: 'customerGroup',
						render: (_, record) => record.customerGroup ?? '-',
					},
					{
						title: 'Nama FBDH',
						render: (_, record) => record.reportTo ?? '-',
					},
					{
						title: 'Nama FRM',
						dataIndex: 'createdBy',
					},
					{
						title: 'Masa Berlaku Customer ID',
						align: 'center',
						render: (_, record) => {
							if (!record.expiredAt) {
								return '-';
							}
							const diff = dayjs(record.expiredAt).diff(dayjs(), 'day');
							const isExpired = diff < 1;

							return (
								<Flex gap={8} align="center" justify="center">
									<span>{isExpired ? 'Expired' : `${diff} hari`}</span>
									{isExpired && (
										<Button
											size="small"
											type="primary"
											onClick={() => extendExpiryModalHook.open({ customer: record })}
										>
											Extend
										</Button>
									)}
								</Flex>
							);
						},
					},
					{
						title: 'Status Prospect Aktif',
						align: 'center',
						width: 1,
						render: (_, record) => {
							if (!record.prospects || record.prospects.length === 0) {
								return '-';
							}
							const activeProspect =
								record.prospects[record.prospects.length - 1];
							return activeProspect.prospectStatus.toUpperCase();
						},
					},
					{
						title: 'Assign FRM',
						width: 1,
						align: 'center',
						render: (_, record) => (
							<Button
								type="link"
								onClick={() => assignFrmModalHook.open({ customer: record })}
							>
								Assign
							</Button>
						),
					},
					{
						title: 'Delete',
						width: 1,
						align: 'center',
						render: (_, record) => (
							<Button
								type="text"
								danger
								icon={<DeleteOutlined />}
								onClick={() => deleteCustomerModalHook.open({ customer: record })}
							/>
						),
					},
				]}
				pagination={{
					total: data?.data.totalCount,
					current: pagination.pageNumber,
					pageSize: pagination.pageSize,
					onChange: (pageNumber, pageSize) => {
						setPagination((current: PaginationState) => ({
							pageSize,
							pageNumber: current.pageSize !== pageSize ? 1 : pageNumber,
						}));
						setQueryParams((current: FleetQueryParams) => ({
							...current,
							pagination: {
								pageSize,
								pageNumber:
									current.pagination?.pageSize !== pageSize ? 1 : pageNumber,
							},
						}));
					},
				}}
			/>

			<ExtendExpiryModal
				modal={extendExpiryModalHook.modal}
				data={extendExpiryModalHook.data}
				onClose={() => extendExpiryModalHook.close()}
			/>

			<AssignFrmModal
				modal={assignFrmModalHook.modal}
				data={assignFrmModalHook.data}
				onClose={() => assignFrmModalHook.close()}
			/>

			<DeleteCustomerModal
				modal={deleteCustomerModalHook.modal}
				data={deleteCustomerModalHook.data}
				onClose={() => deleteCustomerModalHook.close()}
			/>
		</Space>
	);
};

export default AdminCustomerListPage;
